import { CallbackButton } from '@app/app/_components/CallbackButton/CallbackButton';
import { Card } from '@app/app/_components/Card/Card';
import { Input } from '@app/app/_components/Input/Input';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { DeleteIcon } from '@app/app/_icons/DeleteIcon';
import { InsuredTraveler } from '@app/domain/model/eshop/travel/InsuredTraveler';
import { ViolationError } from '@app/lib/form/contracts/ViolationError';
import { useShowErrorState } from '@app/lib/form/hooks/useShowErrorState';
import { isViolationMessage } from '@app/lib/form/isViolationMessage';
import { useTranslations } from 'next-intl';
import { twJoin } from 'tailwind-merge';

interface Props {
    person: InsuredTraveler;
    index: number;
    handleAgeChange: (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleRemovePerson: (id: string) => void;
    ageError?: ViolationError;
}

export function PersonCard({ person, index, handleAgeChange, handleRemovePerson, ageError }: Props) {
    const t = useTranslations();

    const [showError, handleChange] = useShowErrorState(ageError, handleAgeChange(person.id));

    return (
        <Card className="flex flex-col gap-4 p-6">
            <div className="flex flex-row items-center justify-between gap-4">
                <div className="flex flex-row items-center gap-4">
                    <div className="flex size-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-400 text-sm font-medium text-white">
                        {index + 1}
                    </div>
                    <Paragraph isBold>
                        {index === 0 ? t('eshop.travel.primaryPersonAge') : t('eshop.travel.additionalPersonAge')}
                    </Paragraph>
                </div>
                <div className="flex flex-row items-center gap-4">
                    <Input
                        inputClassName="max-w-24"
                        name="age"
                        type="number"
                        value={person.age === 0 ? '' : person.age.toString()}
                        onChange={handleChange}
                        min={0}
                        maxLength={2}
                    />
                    <CallbackButton
                        ignoreFormStatus
                        id={person.id}
                        variant="text"
                        onClick={handleRemovePerson}
                        className={twJoin(
                            'hover:text-primary-600 flex-shrink-0 p-0 text-primary-500',
                            index === 0 && 'invisible',
                        )}
                        aria-label={t('general.delete')}>
                        <DeleteIcon className="size-6" />
                    </CallbackButton>
                </div>
            </div>
            {showError && isViolationMessage(ageError) && (
                <div className="text-sm text-red-500">{t(ageError.message)}</div>
            )}
        </Card>
    );
}
